import { FC } from 'react'
import { ReactComponent as CalendarIcon } from 'src/assets/svg/calendar-line-16.svg'
import { ReactComponent as ExportIcon } from 'src/assets/svg/blue-file-download.svg'
import styles from './multiComparison.module.scss'
import { ComparingCampaigns } from './ComparingCampaigns'
import { MultiDailyMentions } from './MultiDailyMentions'
import { MultiKeyMetrics } from './MultiKeyMetrics'
import { MultiKeywords } from './MultiKeywords'
import { MultiReport } from './interfaces'
import { MultiSentimentSplit } from './MultiSentimentSplit'
import { MultiTopSources } from './MultiTopSources'

interface MultiComparisonProps {
  // What the header renders above the report — the page owns the trail and the
  // view switch, so this view renders only its own title row and widgets.
  header: JSX.Element
  isDownloading: boolean
  onDownload: () => void
  quickCompare?: string | null
  report: MultiReport
}

const COPY = {
  exportComparison: 'Export Comparison',
  // The deck is rendered on demand and takes seconds; a button that says
  // nothing for that long reads as broken.
  exportingComparison: 'Preparing PDF…',
  title: 'Compare Campaigns',
}

/*
 * The report for three or four campaigns.
 *
 * A separate view from the two-campaign one rather than that one widened. Every
 * widget there is built on a primary and a comparison — "higher than
 * comparison", one bar against another, a delta between two numbers — and none
 * of those questions has an answer once there are four. Here the shape is a
 * table or a chart with one series per campaign, and the comparison is made by
 * the reader rather than stated for them.
 */
export const MultiComparison: FC<MultiComparisonProps> = ({
  header,
  isDownloading,
  onDownload,
  quickCompare,
  report,
}) => (
  <>
    {header}

    <div className={styles.titleRow}>
      <h2 className={styles.title}>{COPY.title}</h2>

      <div className={styles.titleActions}>
        <span className={styles.period}>
          <CalendarIcon className={styles.periodIcon} />
          {quickCompare && (
            <span className={styles.periodLabel}>{quickCompare}</span>
          )}
          <span className={styles.periodDates}>{report.periodLabel}</span>
        </span>

        {/* The same deck the paired report prints: its template maps over the
            campaigns too, so three and four need nothing of their own. */}
        <button
          className={styles.exportButton}
          disabled={isDownloading}
          onClick={onDownload}
          type="button"
        >
          <ExportIcon className={styles.exportIcon} />
          {isDownloading ? COPY.exportingComparison : COPY.exportComparison}
        </button>
      </div>
    </div>

    <ComparingCampaigns campaigns={report.campaigns} />

    <MultiKeyMetrics
      campaigns={report.campaigns}
      insight={report.metricsInsight}
      metrics={report.metrics}
    />

    <MultiSentimentSplit
      campaigns={report.campaigns}
      insight={report.sentimentInsight}
      rows={report.sentiment}
    />

    <MultiDailyMentions
      campaigns={report.campaigns}
      days={report.dailyMentions.days}
      series={report.dailyMentions.series}
    />

    <MultiKeywords campaigns={report.campaigns} tables={report.keywords} />

    <MultiTopSources
      campaigns={report.campaigns}
      series={report.topSources.series}
      sources={report.topSources.sources}
    />
  </>
)
